import * as React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  InputAdornment,
  TextField,
  Typography,
  createTheme,
  useMediaQuery,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SearchIcon from "@mui/icons-material/Search";
import { useDocumentsQuery } from "../../queries/useToeicQueries";

const searchFieldSx = {
  "& .MuiOutlinedInput-root": {
    height: 44,
    borderRadius: "999px",
    bgcolor: "var(--toeic-surface)",
    boxShadow: "none",
    "& fieldset": { borderColor: "var(--toeic-line)", borderStyle: "dotted" },
    "&:hover fieldset": { borderColor: "var(--toeic-sky)" },
    "&.Mui-focused fieldset": { borderColor: "var(--toeic-sky)", borderWidth: 1.5 },
  },
  "& .MuiInputAdornment-root": {
    width: 30,
    height: 30,
    maxHeight: 30,
    borderRadius: "999px",
    bgcolor: "#e8f6ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    alignSelf: "center",
    mt: "0 !important",
    mr: 0.75,
  },
  "& .MuiOutlinedInput-input": { py: 0, fontWeight: 500, fontSize: 14 },
  "& .MuiInputLabel-root": { fontWeight: 500 },
  "& .MuiInputLabel-root.Mui-focused": { color: "var(--toeic-sky-deep)" },
};

function Document() {
  const [keySearch, setKeySearch] = React.useState("");
  const { data: documents = [] } = useDocumentsQuery(keySearch);

  const theme = createTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleSearchChange = (e) => {
    setKeySearch(e.target.value);
  };

  return (
    <Box
      sx={{
        width: "100%",
        mt: 4,
        mb: 6,
      }}
    >
      <Box component="header">
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "end",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box className="toeic-reveal">
            <Typography
              component="h1"
              sx={{
                m: 0,
                color: "var(--toeic-ink)",
                fontSize: { xs: 36, md: 56 },
                lineHeight: { xs: 1.05, md: 1 },
                fontWeight: 800,
                letterSpacing: 0,
              }}
            >
              <Box component="span" sx={{ position: "relative", display: "inline-block" }}>
                <Box
                  component="span"
                  aria-hidden="true"
                  sx={{
                    position: "absolute",
                    left: "-0.12em",
                    right: "-0.12em",
                    bottom: "0.04em",
                    height: "0.44em",
                    bgcolor: "rgba(70, 168, 232, 0.28)",
                    borderRadius: "0.45em 0.6em 0.4em 0.55em",
                    transform: "rotate(-1deg)",
                  }}
                />
                <Box component="span" sx={{ position: "relative" }}>
                  TOEIC Documents
                </Box>
              </Box>
              .
            </Typography>
          </Box>
        </Box>
        <Typography
          className="toeic-reveal"
          sx={{
            mt: 2,
            maxWidth: 660,
            color: "var(--toeic-ink-soft)",
            fontSize: 18,
            lineHeight: 1.65,
            fontWeight: 700,
          }}
        >
          Search and download TOEIC practice materials without the heavy dashboard shell.
        </Typography>
      </Box>

      <Box sx={{ mt: 4, display: "grid", gap: 4 }}>
        <Box
          aria-hidden="true"
          sx={{
            borderTop: "1.5px dotted var(--toeic-line)",
            width: "100%",
          }}
        />

        <Box
          className="toeic-reveal"
        sx={{
          p: isMobile ? 2 : 2.5,
          borderRadius: "18px",
          border: "1px solid rgba(215, 234, 245, 0.82)",
          bgcolor: "var(--toeic-paper-grid)",
          boxShadow: "var(--toeic-shadow-card)",
          display: "grid",
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography
            sx={{
              color: "var(--toeic-ink)",
              fontSize: 14,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: ".12em",
            }}
          >
            Document Filter
          </Typography>
          <Typography sx={{ color: "var(--toeic-ink-soft)", fontSize: 12, fontWeight: 700 }}>
            {documents.length} documents
          </Typography>
        </Box>

        <TextField
          label="Search Document"
          variant="outlined"
          fullWidth
          value={keySearch}
          onChange={handleSearchChange}
          sx={searchFieldSx}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "var(--toeic-sky-deep)", fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
        />
        </Box>

        <Box
          aria-hidden="true"
          sx={{
            borderTop: "1.5px dotted var(--toeic-line)",
            width: "100%",
          }}
        />
      </Box>

      <Grid container spacing={2} sx={{ mt: 4 }}>
        {documents.length > 0 ? (
          documents.map((document, index) => (
            <Grid item xs={12} sm={6} md={4} key={document._id || document.document_name}>
              <Card
                elevation={0}
                sx={{
                  height: "100%",
                  borderRadius: "14px",
                  border: 0,
                  bgcolor: "var(--toeic-surface)",
                  boxShadow: "var(--toeic-shadow-card)",
                  transition: "transform 0.22s var(--toeic-ease-fluid), box-shadow 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 24px 44px -30px rgba(19, 43, 63, 0.52)",
                  },
                }}
              >
                <CardContent sx={{ height: "100%", display: "flex", flexDirection: "column", p: 2.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "14px",
                        display: "grid",
                        placeItems: "center",
                        bgcolor: index % 3 === 0 ? "var(--toeic-sky)" : index % 3 === 1 ? "var(--toeic-gold)" : "var(--toeic-mint)",
                        color: index % 3 === 1 ? "var(--toeic-ink)" : "#ffffff",
                        fontWeight: 800,
                        fontSize: 22,
                      }}
                    >
                      {String(index + 1).padStart(2, "0")}
                    </Box>
                    <Chip
                      size="small"
                      icon={<DescriptionIcon />}
                      label="PDF"
                      sx={{
                        bgcolor: "rgba(70, 168, 232, 0.12)",
                        color: "var(--toeic-sky-deep)",
                        "& .MuiChip-icon": { color: "var(--toeic-sky-deep)" },
                      }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      color: "var(--toeic-ink)",
                      fontWeight: 700,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {document.document_name}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 1,
                      color: "var(--toeic-ink-soft)",
                      fontWeight: 700,
                      lineHeight: 1.55,
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {document.document_content}
                  </Typography>
                  <Button
                    component="a"
                    href={document.document_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="contained"
                    startIcon={<FileDownloadIcon />}
                    sx={{
                      mt: "auto",
                      justifyContent: "space-between",
                      pt: 1.2,
                      bgcolor: "transparent",
                      color: "var(--toeic-ink)",
                      boxShadow: "none",
                      borderTop: "1px dotted var(--toeic-line)",
                      borderRadius: 0,
                      px: 0,
                      "&:hover": { bgcolor: "transparent", color: "var(--toeic-sky-deep)", boxShadow: "none" },
                    }}
                  >
                    Download
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))
        ) : (
          <Grid item xs={12}>
            <Box
              sx={{
                p: 4,
                textAlign: "center",
                borderRadius: "18px",
                border: "1.5px dashed var(--toeic-line)",
                bgcolor: "var(--toeic-surface)",
              }}
            >
              <Typography sx={{ color: "var(--toeic-ink-soft)", fontWeight: 800 }}>No document available</Typography>
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

export default Document;
